import { cn, useTranslation } from '@hbs-us/ui';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { Search, X } from 'lucide-react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAccess } from '../../auth/useAccess.js';
import { useFeatureAccess } from '../../auth/useFeatureAccess.js';
import {
  buildGroups,
  type PaletteDestination,
  type PaletteRow,
} from '../../features/search/buildGroups.js';
import {
  MIN_QUERY_LENGTH,
  PER_TYPE_LIMIT,
  type SearchType,
} from '../../features/search/contractsMirror.js';
import { SEARCH_CATALOG } from '../../features/search/searchCatalog.js';
import { useSearchQuery } from '../../features/search/useSearchQuery.js';
import { useDebounced } from '../../lib/useDebounced.js';
import { CHROME_DESTINATIONS, LAUNCHER_GROUPS } from '../navData.js';
import { FilterButton, KeyHint, PaletteRowButton } from './SearchPanelRows.js';

/**
 * "Search everything" — one panel over every record this session may reach.
 *
 * THE ACCESS RULE IS THE WHOLE DESIGN. `GET /api/v1/search` gates each record
 * TYPE with the same rule that type's own list route uses, and returns the types
 * it allowed. This panel narrows that list again through `useAccess`, so the two
 * gates can only ever agree on the stricter answer — a palette that listed a
 * record from a screen the role hides would be a way around the nav.
 *
 * ONE ROUND TRIP, NOT ONE PER FILTER. The endpoint answers every allowed type at
 * once and sends the per-type counts with them, so narrowing to a type is
 * instant and costs nothing. `useSearchQuery` explains why no `type` parameter
 * is ever sent.
 *
 * TWO MARKERS TRAVEL, NOTHING FLICKERS. The selected row and the active filter
 * are each ONE element that MOVES, rather than a fill that every row and every
 * button paints for itself: a marker that travels reads as a cursor, while
 * per-item fills read as a flicker. Both are decoration over content that is
 * fully rendered without them, so a browser running no transitions loses the
 * movement and nothing else.
 */

/** One request per pause in typing. Short: this is a keystroke-level surface. */
const DEBOUNCE_MS = 180;

export interface SearchPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchPanel({ open, onOpenChange }: SearchPanelProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const canAccess = useAccess();
  const canSeeFeature = useFeatureAccess();

  const [query, setQuery] = useState('');
  const [activeType, setActiveType] = useState<SearchType | 'all'>('all');
  const [selected, setSelected] = useState(0);
  const [expanded, setExpanded] = useState<ReadonlySet<SearchType>>(() => new Set());
  const [atListEnd, setAtListEnd] = useState(true);

  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLSpanElement>(null);
  const filterInkRef = useRef<HTMLSpanElement>(null);

  const debounced = useDebounced(query, DEBOUNCE_MS);
  const trimmed = debounced.trim();
  const search = useSearchQuery(debounced, open);

  // A fresh panel opens on an empty query, with nothing scoped or expanded.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveType('all');
    setSelected(0);
    setExpanded(new Set());
  }, [open]);

  const availableTypes = useMemo(
    () => (search.data?.types ?? []).filter((type) => canAccess(SEARCH_CATALOG[type].access)),
    [search.data?.types, canAccess],
  );

  /**
   * The filter row offers the types that FOUND something, and nothing else.
   *
   * Rendering all nine with a 0 beside the empty ones was the mockup's idea and
   * it does not survive contact with the real vocabulary: ten buttons overrun a
   * 688px panel, and the row then scrolls a clipped word off the right edge with
   * no affordance saying so. Every button here does something when pressed.
   */
  const filterTypes = useMemo(
    () => availableTypes.filter((type) => (search.data?.counts?.[type] ?? 0) > 0),
    [availableTypes, search.data?.counts],
  );

  /** What the whole search found, honest about the server's per-type ceiling. */
  const found = useMemo(() => {
    const counts = availableTypes.map((type) => search.data?.counts?.[type] ?? 0);
    return {
      total: counts.reduce((sum, count) => sum + Math.min(count, PER_TYPE_LIMIT), 0),
      capped: counts.some((count) => count > PER_TYPE_LIMIT),
    };
  }, [availableTypes, search.data?.counts]);

  // Destinations are the ONE nav list under its own two gates, matched on the
  // label so "payroll" offers the screen as well as the records on it. With no
  // query they are the whole list, which is what the panel opens on.
  const destinations: PaletteDestination[] = useMemo(() => {
    const needle = trimmed.toLowerCase();
    return [
      ...CHROME_DESTINATIONS.filter((destination) => destination.to === '/'),
      ...LAUNCHER_GROUPS.flatMap((group) => group.items),
      ...CHROME_DESTINATIONS.filter((destination) => destination.to !== '/'),
    ]
      .filter((destination) => canSeeFeature(destination.to))
      .filter((destination) => canAccess(destination.access))
      .map((destination) => ({
        to: destination.to,
        label: t(destination.labelKey),
        icon: destination.icon,
      }))
      .filter((destination) => !needle || destination.label.toLowerCase().includes(needle));
  }, [trimmed, canAccess, canSeeFeature, t]);

  const { groups, rows } = useMemo(
    () =>
      buildGroups({
        response: search.data,
        availableTypes,
        activeType,
        expanded,
        labelForType: (type) => t(SEARCH_CATALOG[type].labelKey),
        destinations,
        destinationsLabel: t('nav:search.goTo'),
      }),
    [search.data, availableTypes, activeType, expanded, destinations, t],
  );

  /** Row key → its place in the flat list, so a hover is a lookup, not a scan. */
  const rowIndex = useMemo(() => new Map(rows.map((row, index) => [row.key, index])), [rows]);

  // The highlighted row is gone the moment the list shrinks under it.
  useEffect(() => {
    setSelected((index) => Math.min(index, Math.max(0, rows.length - 1)));
  }, [rows.length]);

  // Both markers are MEASURED from the elements they follow, so neither can
  // drift out of step with a row that wrapped or a label that is longer in
  // Spanish. `top: 0` / `left: 0` are set in the class on purpose: an absolutely
  // positioned element with only a transform inherits its static position and
  // lands wherever the previous sibling left it.
  //
  // The deps are a RE-RUN SIGNAL, not values the body closes over: it reads the
  // DOM, and the DOM changes when the selection or the list does.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useLayoutEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;
    const active = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!active) {
      cursor.hidden = true;
      return;
    }
    cursor.hidden = false;
    cursor.style.height = `${active.offsetHeight}px`;
    cursor.style.transform = `translateY(${active.offsetTop}px)`;
    // jsdom does not implement it, and neither does every embedded webview.
    active.scrollIntoView?.({ block: 'nearest' });
  }, [selected, rows]);

  // Same again: measured from the DOM, re-run when the filter row changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useLayoutEffect(() => {
    const ink = filterInkRef.current;
    const active = filtersRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!ink || !active) return;
    // Inset by the button's own horizontal padding, so the rule sits under the
    // WORD rather than under the whole hit area.
    ink.style.width = `${Math.max(0, active.offsetWidth - 20)}px`;
    ink.style.transform = `translateX(${active.offsetLeft + 10}px)`;
  }, [activeType, filterTypes]);

  // The bottom fade says "there is more below", so it has to LIFT at the end of
  // the list. A fade that never lifts just dims the last row for good, and reads
  // as clipped content rather than as an affordance.
  const measureListEnd = () => {
    const list = listRef.current;
    if (!list) return;
    setAtListEnd(list.scrollTop + list.clientHeight >= list.scrollHeight - 2);
  };
  // Re-measure whenever the rendered list changes height, which is what `rows`
  // stands in for here.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useLayoutEffect(measureListEnd, [rows]);

  const scopeTo = (type: SearchType | 'all') => {
    setActiveType(type);
    setSelected(0);
    setExpanded(new Set());
  };

  const choose = (row: PaletteRow) => {
    if (row.kind === 'more') {
      setExpanded((current) => new Set(current).add(row.type));
      return;
    }
    const to = row.kind === 'hit' ? SEARCH_CATALOG[row.hit.type].path(row.hit) : row.destination.to;
    onOpenChange(false);
    // `to` is built from the ONE catalog of real routes; the cast is the same
    // trade router.tsx makes for a path it only knows as a string.
    void navigate({ to } as never);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && rows.length) {
      event.preventDefault();
      setSelected((index) => (index + 1) % rows.length);
    } else if (event.key === 'ArrowUp' && rows.length) {
      event.preventDefault();
      setSelected((index) => (index - 1 + rows.length) % rows.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const row = rows[selected];
      if (row) choose(row);
    } else if (event.key === 'Backspace' && !query && activeType !== 'all') {
      // Drop the type filter before deleting text, so one Backspace undoes the
      // narrowing you just did instead of eating the query behind it.
      event.preventDefault();
      scopeTo('all');
    }
  };

  const activeRow = rows[selected];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-background/70 backdrop-blur-[2px] backdrop-saturate-[.92]" />
        <Dialog.Content
          aria-describedby={undefined}
          /*
           * The panel exists to be typed into, so opening it puts the caret in
           * the field. Doing it here rather than with an `autoFocus` attribute
           * means it stays the FIELD that gets focus even after a control is
           * added ahead of it — Radix's own default is "the first tabbable
           * thing", which the scope chip's clear button would quietly become.
           */
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
          className={cn(
            'fixed top-[11vh] left-1/2 z-50 flex max-h-[74vh] w-[min(43rem,calc(100vw-2rem))]',
            '-translate-x-1/2 flex-col overflow-hidden rounded-xl border border-border bg-card',
            'shadow-elev-3 focus-visible:outline-none',
            // Settles from a transform. The resting state IS the visible one, so
            // a browser that never runs this still shows the whole panel.
            'motion-safe:animate-[qelm-panel-in_var(--dur-base)_var(--ease-out)_both]',
          )}
        >
          <Dialog.Title className="sr-only">{t('nav:search.everything')}</Dialog.Title>

          <div className="flex h-14 shrink-0 items-center gap-3 border-border border-b px-4">
            <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            {activeType !== 'all' ? (
              <span className="flex shrink-0 items-center gap-1 rounded-md bg-secondary py-1 pr-1 pl-2.5 font-medium text-secondary-foreground text-xs">
                {t('nav:search.scope', { type: t(SEARCH_CATALOG[activeType].labelKey) })}
                <button
                  type="button"
                  onClick={() => scopeTo('all')}
                  aria-label={t('nav:search.clearFilter')}
                  className="rounded-sm p-0.5 text-muted-foreground transition-colors duration-[var(--dur-quick)] ease-[var(--ease-out)] hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card motion-reduce:transition-none"
                >
                  <X className="size-3" aria-hidden="true" />
                </button>
              </span>
            ) : null}
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded
              aria-controls={listboxId}
              aria-activedescendant={activeRow ? `${listboxId}-${activeRow.key}` : undefined}
              aria-label={t('nav:search.everything')}
              autoComplete="off"
              spellCheck={false}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setSelected(0);
                setExpanded(new Set());
              }}
              onKeyDown={onKeyDown}
              placeholder={t('nav:search.everything')}
              className="h-full min-w-0 flex-1 border-0 bg-transparent text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            <kbd className="hidden shrink-0 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[0.6875rem] text-muted-foreground sm:inline">
              esc
            </kbd>
          </div>

          {filterTypes.length ? (
            <div
              ref={filtersRef}
              role="toolbar"
              aria-label={t('nav:search.filterByType')}
              className="relative flex shrink-0 gap-0.5 overflow-x-auto border-border border-b px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <FilterButton
                label={t('nav:search.all')}
                count={found.total}
                capped={found.capped}
                pressed={activeType === 'all'}
                onPress={() => scopeTo('all')}
              />
              {filterTypes.map((type) => {
                const perType = search.data?.counts?.[type] ?? 0;
                return (
                  <FilterButton
                    key={type}
                    label={t(SEARCH_CATALOG[type].labelKey)}
                    count={Math.min(perType, PER_TYPE_LIMIT)}
                    capped={perType > PER_TYPE_LIMIT}
                    pressed={activeType === type}
                    onPress={() => scopeTo(type)}
                  />
                );
              })}
              {/* ONE indicator that travels, not a rule each button grows for itself. */}
              <span
                ref={filterInkRef}
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 left-0 h-0.5 w-0 rounded-t-full bg-primary transition-[transform,width] duration-[var(--dur-base)] ease-[var(--ease-out)] motion-reduce:transition-none"
              />
            </div>
          ) : null}

          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label={t('nav:search.everything')}
            onScroll={measureListEnd}
            className={cn(
              // NO TOP PADDING. A sticky heading sticks to the scrollport's
              // PADDING edge, so a `py-` here leaves a transparent band above
              // every stuck heading that scrolling rows show through — measured
              // at 6px, and it reads as a sliced row hanging over the filters.
              // The first heading's own `pt-2.5` gives the list its top air.
              'relative min-h-[7.5rem] flex-1 overflow-y-auto pb-1.5',
              !atListEnd &&
                '[-webkit-mask-image:linear-gradient(black_calc(100%-1.25rem),transparent)] [mask-image:linear-gradient(black_calc(100%-1.25rem),transparent)]',
            )}
          >
            {/* The selection is ONE element that travels down the list. */}
            <span
              ref={cursorRef}
              aria-hidden="true"
              className="pointer-events-none absolute top-0 right-1.5 left-1.5 z-0 rounded-lg bg-secondary ring-1 ring-border transition-[transform,height] duration-[var(--dur-quick)] ease-[var(--ease-out)] motion-reduce:transition-none"
            />
            {rows.length ? (
              groups.map((group) => (
                // A listbox groups its options with role="group".
                // biome-ignore lint/a11y/useSemanticElements: <fieldset> is not valid inside a listbox
                <section key={group.key} role="group" aria-label={group.label}>
                  {/* Sticks as its own rows scroll under it, so you never lose
                      track of which type you are reading. */}
                  <div className="sticky top-0 z-20 flex items-baseline gap-2 bg-card px-4 pt-3 pb-2">
                    <h2 className="font-semibold text-foreground text-xs tracking-wide">
                      {group.label}
                    </h2>
                    <span className="font-mono text-[0.6875rem] text-muted-foreground">
                      {group.capped ? `${group.count}+` : group.count}
                    </span>
                  </div>
                  {group.rows.map((row) => (
                    <PaletteRowButton
                      key={row.key}
                      id={`${listboxId}-${row.key}`}
                      row={row}
                      query={trimmed}
                      locale={i18n.language}
                      selected={activeRow?.key === row.key}
                      onSelect={() => choose(row)}
                      onHover={() => setSelected(rowIndex.get(row.key) ?? 0)}
                      moreLabel={t('nav:search.showMore', {
                        count: row.kind === 'more' ? row.remaining : 0,
                      })}
                    />
                  ))}
                </section>
              ))
            ) : (
              <p className="px-6 py-10 text-center text-muted-foreground text-sm leading-relaxed">
                <EmptyMessage
                  failed={search.isError}
                  searching={search.isFetching && !search.data}
                  query={trimmed.length >= MIN_QUERY_LENGTH ? trimmed : ''}
                />
              </p>
            )}
          </div>

          {/* The filter row already reports what was found, and reports it more
              honestly than a total of the rows that happen to be on screen. So
              the footer carries the keys and nothing else. */}
          <div className="hidden shrink-0 items-center gap-4 border-border border-t bg-background px-4 py-2 text-muted-foreground text-xs sm:flex">
            {/* A key that does nothing right now is not a hint. With an empty
                list there is nothing to move through and nothing to open, so
                only the key that still works is offered. */}
            {rows.length ? (
              <>
                <KeyHint keys={['↑', '↓']} label={t('nav:search.hintNavigate')} />
                <KeyHint keys={['↵']} label={t('nav:search.hintOpen')} />
              </>
            ) : null}
            <KeyHint keys={['esc']} label={t('nav:search.hintClose')} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Why the list is empty. The four answers are genuinely different, and telling
 * a failed request from "nothing matched" is the difference between retyping and
 * retrying.
 */
function EmptyMessage({
  failed,
  searching,
  query,
}: {
  failed: boolean;
  searching: boolean;
  query: string;
}) {
  const { t } = useTranslation();
  if (failed) return <>{t('nav:search.failed')}</>;
  if (searching) return <>{t('nav:search.searching')}</>;
  if (!query) return <>{t('nav:search.startHint')}</>;
  return (
    <>
      <strong className="mb-1 block font-semibold text-base text-foreground">
        {t('nav:search.noMatch')}
      </strong>
      {t('nav:search.noMatchBody', { query })}
    </>
  );
}
